import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const UserIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
  </svg>
);

const EnvelopeIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
  </svg>
);

const UsersIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const HandRaisedIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M10.05 4.575a1.575 1.575 0 10-3.15 0v3m3.15-3v-1.5a1.575 1.575 0 013.15 0v1.5m-3.15 0l.075 5.925m3.075.75V4.575m0 0a1.575 1.575 0 013.15 0V15M6.9 7.575a1.575 1.575 0 10-3.15 0v8.175a6.75 6.75 0 006.75 6.75h2.018a5.25 5.25 0 003.712-1.538l1.732-1.732a5.25 5.25 0 001.538-3.712l.003-2.024a.668.668 0 01.198-.471 1.575 1.575 0 10-2.228-2.228 3.818 3.818 0 00-1.12 2.687M6.9 7.575V12m6.27 4.318A4.49 4.49 0 0116.35 15m.002 0h-.002" />
  </svg>
);

const HeartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const StaffCard = ({ name, role, emailUrl, imageUrl }) => (
  <div className="flex flex-col items-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300">
    <div className="w-24 h-24 rounded-full overflow-hidden bg-gray-100 border-4 border-school-gold/20 shadow-sm mb-6">
      {imageUrl ? (
        <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center bg-school-blue/8 text-school-blue">
          <UserIcon className="w-12 h-12" />
        </div>
      )}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-2 tracking-tight text-center">
      {name}
    </h3>
    <p className="text-base text-school-goldDark font-medium mb-4">{role}</p>
    <a
      href={emailUrl}
      className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-school-blue/8 text-school-blue text-sm font-semibold hover:bg-school-blue hover:text-white transition-colors"
    >
      <EnvelopeIcon />
      Send Email
    </a>
  </div>
);

const FeatureCard = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-3 tracking-tight leading-tight">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7 max-w-sm mx-auto">{description}</p>
  </div>
);

// ==================== MAIN COMPONENT ====================

const Paraprofessionals = () => {
  const staff = [
    {
      name: "Mildred Almonte",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/8757/21/11/09/124146-e9ec159f-e330-454a-8cb5-3066f7175dfe.jpg"
    },
    {
      name: "Linda Alvarez Cruz",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Blanca Angel",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/2706/21/11/18/123934-a13ae3c2-1bc8-492a-9d9b-fc8a21f1dc45.jpg"
    },
    {
      name: "V. Aza",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "J. Balis",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Z. Benitez",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Doris Camacho",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/251d/22/10/17/151108-6fecd83b-a556-4c06-9008-89d502b7f46f.png"
    },
    {
      name: "Crystal Cogmon",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/e27a/22/10/31/190949-c1d0e93d-b1ec-4b7d-8118-d28be5ffbbd4.jpeg"
    },
    {
      name: "Y. Colon",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "N. Garcia",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Maria Garcia Abreu",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "E. Gregorio",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Merienle Martinez",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "D. Montilla",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Lauren Moore",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "J. Pena",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "T. Robinson",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    },
    {
      name: "Amy Segura",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/03ee/21/11/18/123817-6dfb4bf8-f077-400b-b655-6ebb04994656.jpg"
    },
    {
      name: "Andres Silva",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/77a7/22/11/02/112850-67db6f06-c45f-4f54-9ebe-05ed54aefe1c.jpg"
    },
    {
      name: "Manuela Tavarez",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/141c/21/11/18/130500-adea5b5a-3baa-499c-b586-58d9b9ddf6ff.jpg"
    },
    {
      name: "Aura Vasquez",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: "https://3.files.edl.io/74e0/21/11/18/124415-b800f392-819a-4b7d-8c16-f467a3c03496.jpg"
    },
    {
      name: "Donna Vuong Kien",
      role: "Para",
      emailUrl: "/coming-soon",
      imageUrl: null
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-school-gold" aria-hidden="true" />
                Contact Us
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                Paraprofessionals
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Supporting student success through dedicated classroom assistance
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== INTRODUCTION ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Our Team"
              title="Dedicated Support Staff"
              description="Our paraprofessionals work closely with teachers to provide individualized support and ensure every student has the assistance they need to succeed"
            />
          </Container>
        </section>

        {/* ==================== SERVICES ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="What We Do"
              title="Supporting Student Learning"
              description="Our paraprofessionals play a vital role in the classroom"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              <FeatureCard
                icon={<UsersIcon />}
                title="Classroom Support"
                description="Working alongside teachers to provide individual and small group instruction."
              />

              <FeatureCard
                icon={<HandRaisedIcon />}
                title="Student Assistance"
                description="Helping students with academic tasks, behavioral support, and daily routines."
              />

              <FeatureCard
                icon={<HeartIcon />}
                title="Personalized Care"
                description="Building meaningful relationships with students to foster a positive learning environment."
              />
            </div>
          </Container>
        </section>

        {/* ==================== STAFF SECTION ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Meet the Team"
              title="Our Paraprofessionals"
              description="Committed to supporting every student's educational journey"
            />

            <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {staff.map((member, index) => (
                <StaffCard key={index} {...member} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Questions About Support Services?
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              Connect with our paraprofessional team to learn more about student support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/coming-soon"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                Contact Us
              </a>
              <a
                href="/"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Home
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default Paraprofessionals;
